import { inject, Injectable } from "@angular/core";
import {
  EnergyContact,
  IotDeviceInfo,
  IotSystemInfo,
  PlantData,
} from "./models/system-management.model";
import { BehaviorSubject, Observable, ReplaySubject } from "rxjs";
import { User } from "app/core/user/user.types";
import { TranslocoService } from "@jsverse/transloco";

@Injectable({
  providedIn: "root",
})
export class SystemManagementStateService {
  private translocoService = inject(TranslocoService);
  private langMap: Record<string, string> = {
    Deutsch: "de",
    Englisch: "en-GB",
  };
  constructor() {}
  private readonly bifiKey = "BifiInfo";
  private readonly systemNameKey = "SystemName";
  private readonly systemInfoIdKey = "SystemInfoId";
  private readonly crmSystemNameIdKey = "CrmSystemInfoId";
  private readonly userInfoKey = "userInfo";
  private readonly iotSystemInfoKey = "iotSystemInfo";
  private readonly energyContactKey = "energyConatctInfo";

  private readonly bifiInfoSubject = new BehaviorSubject<IotDeviceInfo | null>(
    null,
  );
  private readonly systemNameSubject = new BehaviorSubject<string | null>(null);
  private readonly systemInfoIdSubject = new BehaviorSubject<string | null>(
    null,
  );
  private readonly selectedDevicesSubject = new BehaviorSubject<
    IotDeviceInfo[]
  >([]);
  private readonly userInfoSubject = new BehaviorSubject<User | null>(null);
  private readonly crmSystemNameIdSubject =
    new BehaviorSubject<PlantData | null>(null);
  private readonly iotSystemInfoSubject =
    new BehaviorSubject<IotSystemInfo | null>(null);
  private readonly energyContactSubject =
    new BehaviorSubject<EnergyContact | null>(null);

  bifiInfoSubject$: Observable<IotDeviceInfo> =
    this.bifiInfoSubject.asObservable();
  systemNameSubject$: Observable<string> =
    this.systemNameSubject.asObservable();
  systemInfoIdSubject$: Observable<string> =
    this.systemInfoIdSubject.asObservable();
  selectedDevices$: Observable<IotDeviceInfo[]> =
    this.selectedDevicesSubject.asObservable();
  userInfoSubject$: Observable<User> = this.userInfoSubject.asObservable();
  crmSystemNameIdSubject$: Observable<PlantData> =
    this.crmSystemNameIdSubject.asObservable();
  iotSystemInfoSubject$: Observable<IotSystemInfo> =
    this.iotSystemInfoSubject.asObservable();
  energyContactSubject$: Observable<EnergyContact> =
    this.energyContactSubject.asObservable();

  setBifiInfo(value: IotDeviceInfo) {
    localStorage.setItem(this.bifiKey, JSON.stringify(value));
    this.bifiInfoSubject.next(value);
  }

  getBifiInfo(): IotDeviceInfo | null {
    if (this.bifiInfoSubject.getValue() === null) {
      const storedData = localStorage.getItem(this.bifiKey);
      const parsedData = storedData ? JSON.parse(storedData) : null;
      this.bifiInfoSubject.next(parsedData);
    }

    return this.bifiInfoSubject.getValue();
  }

  setSystemName(value: string) {
    localStorage.setItem(this.systemNameKey, value);
    this.systemNameSubject.next(value);
  }

  getSystemName(): string | null {
    if (this.systemNameSubject.getValue() === null) {
      const storedData = localStorage.getItem(this.systemNameKey);
      const parsedData = storedData ? storedData : "";
      this.systemNameSubject.next(parsedData);
    }

    return this.systemNameSubject.getValue();
  }

  setSystemInfoId(value: string) {
    localStorage.setItem(this.systemInfoIdKey, value);
    this.systemInfoIdSubject.next(value);
  }

  getSystemInfoId(): string | null {
    if (this.systemInfoIdSubject.getValue() === null) {
      const storedData = localStorage.getItem(this.systemInfoIdKey);
      const parsedData = storedData ? storedData : "";
      this.systemInfoIdSubject.next(parsedData);
    }

    return this.systemInfoIdSubject.getValue();
  }

  setPlantData(value: PlantData) {
    localStorage.setItem(this.crmSystemNameIdKey, JSON.stringify(value));
    this.crmSystemNameIdSubject.next(value);
  }

  getPlantData(): PlantData | null {
    if (this.crmSystemNameIdSubject.getValue() === null) {
      const storedData = localStorage.getItem(this.crmSystemNameIdKey);
      const parsedData: PlantData | null = storedData
        ? JSON.parse(storedData)
        : null;
      if (parsedData) {
        this.crmSystemNameIdSubject.next(parsedData);
      }
    }

    return this.crmSystemNameIdSubject.getValue();
  }

  setEnergyContcatInfo(value: EnergyContact) {
    localStorage.setItem(this.energyContactKey, JSON.stringify(value));
    this.energyContactSubject.next(value);
  }

  getEnergyContactInfo(): EnergyContact | null {
    if (this.energyContactSubject.getValue() === null) {
      const storedData = localStorage.getItem(this.energyContactKey);
      const parsedData: EnergyContact | null = storedData
        ? JSON.parse(storedData)
        : null;
      if (parsedData) {
        this.energyContactSubject.next(parsedData);
      }
    }

    return this.energyContactSubject.getValue();
  }

  setIotSystemInfoData(value: IotSystemInfo) {
    localStorage.setItem(this.iotSystemInfoKey, JSON.stringify(value));
    this.iotSystemInfoSubject.next(value);
  }

  getIotSystemInfoData(): IotSystemInfo | null {
    if (this.iotSystemInfoSubject.getValue() === null) {
      const storedData = localStorage.getItem(this.iotSystemInfoKey);
      const parsedData: IotSystemInfo | null = storedData
        ? JSON.parse(storedData)
        : null;
      if (parsedData) {
        this.iotSystemInfoSubject.next(parsedData);
      }
    }

    return this.iotSystemInfoSubject.getValue();
  }

  setSelectedDevices(devices: IotDeviceInfo[]): void {
    this.selectedDevicesSubject.next(devices);
  }

  getSelectedDevices(): IotDeviceInfo[] {
    return this.selectedDevicesSubject.getValue();
  }

  setUserInfo(value: User) {
    localStorage.setItem(this.userInfoKey, JSON.stringify(value));

    this.userInfoSubject.next(value);
  }

  getUserInfo(): User | null {
    if (this.userInfoSubject.getValue() === null) {
      const storedData = localStorage.getItem(this.userInfoKey);
      const parsedData = storedData ? JSON.parse(storedData) : null;
      this.userInfoSubject.next(parsedData);
    }
    return this.userInfoSubject.getValue();
  }

  setLanguage(userLang: string) {
    const normalizedUserLang = String(userLang ?? "").trim();
    const langCode = this.langMap[normalizedUserLang] || "en-GB"; // fallback to English
    this.translocoService.setActiveLang(langCode);
  }
}
